import { use, useCallback, useMemo } from 'react';
import { useUrl } from 'crossroad';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import ContextDropdown from '../context-dropdown';
import ancientIcon from '@/assets/context-panel-ancient.png';
import biblicalIcon from '@/assets/context-panel-biblical.png';
import modernIcon from '@/assets/context-panel-modern.png';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { Analytics } from '@/lib/analytics';

interface Props {
    passageId: string;
    onBeginGuidedStudy?: () => void;
}

export function PassageContextFooter({ passageId, onBeginGuidedStudy }: Props) {
    const [url, setUrl] = useUrl();
    const isDarkMode = useDarkMode();
    const { data: sectionData } = use(PassageDataContext);

    const { previousSection, nextSection } = useMemo(() => {
        if (!sectionData?.items) {
            return { previousSection: null, nextSection: null };
        }

        const currentIndex = sectionData.items.findIndex(s => s.id === passageId);
        if (currentIndex === -1) {
            return { previousSection: null, nextSection: null };
        }

        const previousSection = currentIndex > 0 ? sectionData.items[currentIndex - 1] : null;
        const nextSection = currentIndex < sectionData.items.length - 1 ? sectionData.items[currentIndex + 1] : null;

        return { previousSection, nextSection };
    }, [sectionData?.items, passageId]);

    const formatSectionLabel = (section: Passage | null, prefix: string) => {
        if (!section) return prefix;
        return `${prefix}: ${section.book} ${section.ref}`;
    };

    const handlePrevious = useCallback(() => {
        if (previousSection) {
            Analytics.passageNavigation('previous', passageId, previousSection.id, previousSection.book, previousSection.ref);
            const newUrl = url.path.replace(`/${passageId}`, `/${previousSection.id}`);
            setUrl(newUrl);
        }
    }, [previousSection, setUrl, url.path, passageId]);

    const handleNext = useCallback(() => {
        if (nextSection) {
            Analytics.passageNavigation('next', passageId, nextSection.id, nextSection.book, nextSection.ref);
            const newUrl = url.path.replace(`/${passageId}`, `/${nextSection.id}`);
            setUrl(newUrl);
        }
    }, [nextSection, setUrl, url.path, passageId]);

    // Defer rendering until section data is ready to prevent layout flicker
    // This ensures context links don't jump when Prev/Next buttons expand with labels
    if (!sectionData) {
        return <div className="h-24 w-full" />;
    }

    return (
        <div className="flex items-center justify-between gap-1 border-b border-border h-24 p-2">
            <Button variant="outline" size="sm" onClick={handlePrevious} disabled={!previousSection} className="gap-1 text-xs text-muted-foreground">
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{formatSectionLabel(previousSection, 'Prev')}</span>
                <span className="sm:hidden"></span>
            </Button>

            <ContextDropdown category="ancient" passageId={passageId}>
                <Button variant="ghost" aria-label="Ancient Context menu" title="Explore Ancient Context" className="h-auto">
                    <div className="flex flex-col items-center justify-center gap-2">
                        <div className={`w-8 h-8 ${isDarkMode ? 'filter invert' : ''}`} style={{ backgroundImage: `url(${ancientIcon})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
                        <div>Ancient <br className="md:hidden" />Context</div>
                    </div>
                </Button>
            </ContextDropdown>

            <ContextDropdown category="biblical" passageId={passageId}>
                <Button variant="ghost" aria-label="Biblical Context menu" title="Explore Biblical Context" className="h-auto">
                    <div className="flex flex-col items-center justify-center gap-2">
                        <div className={`w-8 h-8 ${isDarkMode ? 'filter invert' : ''}`} style={{ backgroundImage: `url(${biblicalIcon})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
                        <div>Biblical <br className="md:hidden" />Context</div>
                    </div>
                </Button>
            </ContextDropdown>

            <ContextDropdown category="modern" passageId={passageId} onBeginGuidedStudy={onBeginGuidedStudy}>
                <Button variant="ghost" aria-label="Modern Context menu" title="Explore Modern Context" className="h-auto">
                    <div className="flex flex-col items-center justify-center gap-2">
                        <div className={`w-8 h-8 ${isDarkMode ? 'filter invert' : ''}`} style={{ backgroundImage: `url(${modernIcon})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
                        <div>Modern <br className="md:hidden" />Context</div>
                    </div>
                </Button>
            </ContextDropdown>

            <Button variant="outline" size="sm" onClick={handleNext} disabled={!nextSection} className="gap-1 text-xs text-muted-foreground">
                <span className="hidden sm:inline">{formatSectionLabel(nextSection, 'Next')}</span>
                <span className="sm:hidden"></span>
                <ChevronRight className="h-4 w-4" />
            </Button>
        </div>
    );
}

